import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import formatName from "helpers/formatName";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import MaskCPFCNPJ from "helpers/maskCpfCnpj";
import { useNavigate } from "react-router-dom";
import Loading from "components/Loading";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Tooltip } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import { loadEmpresas, loadClientesEmpresa } from "store/actions/empresas";
import EmpresasCadastro from "./empresasCadastro";
import { AddLink, DeleteOutlineOutlined, KeyboardArrowLeftOutlined, KeyboardArrowRightOutlined, ManageAccounts, PhoneIphone, ReadMore, WhatsApp } from "@mui/icons-material";
import ModalComponent from "components/Modal";
import * as types from "../../store/types/empresas";
import { ActionExcluirEmpresaEClientes, ActionDefinirEmpresaCadastroApp } from "store/actions/empresas";
import { CustomTooltip } from "components/CustomTooltip";
import ModalCriarCredenciado from "./ModalCriarCredenciado";

/* ─── Tokens ─── */
const T = {
  bg: "#f7f8fa",
  card: "#ffffff",
  border: "#eaecf0",
  borderHover: "#d0d5dd",
  text: "#0f1117",
  muted: "#6b7280",
  faint: "#9ca3af",
  accent: "#111827",
  danger: "#dc2626",
  purple: "#7c3aed",
  blue: "#2563eb",
  green: "#16a34a",
  radius: "12px",
  radiusSm: "8px",
};

/* ─── Botões ─── */
const Btn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  gap: 6px; font-family: inherit; font-weight: 600; cursor: pointer;
  transition: all 0.15s ease; border: none;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const BtnPrimary = styled(Btn)`
  background: #111827; color: #fff;
  padding: 9px 18px; border-radius: ${T.radiusSm}; font-size: 13px;
  &:hover:not(:disabled) { background: #374151; }
`;

const BtnOutline = styled(Btn)`
  background: #fff; color: ${T.text};
  padding: 9px 18px; border-radius: ${T.radiusSm}; font-size: 13px;
  border: 1.5px solid ${T.border};
  &:hover:not(:disabled) { border-color: ${T.borderHover}; background: #f9fafb; }
`;

const BtnGhost = styled(Btn)`
  background: transparent; color: ${T.muted};
  padding: 7px 14px; border-radius: ${T.radiusSm}; font-size: 13px;
  &:hover:not(:disabled) { background: #f3f4f6; color: ${T.text}; }
`;

const IconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px;
  border: 1px solid transparent; background: transparent;
  cursor: pointer; transition: all 0.15s; color: ${T.muted};
  font-size: 20px;
  &:hover { background: #f3f4f6; border-color: ${T.border}; }
`;

/* ─── Layout da página ─── */
const PageWrap = styled.div`
  padding: 0 0 40px;
`;

const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 24px;
`;

const PageTitle = styled.div`
  font-size: 20px; font-weight: 700; color: ${T.text}; letter-spacing: -0.3px;
`;

const PageSub = styled.div`
  font-size: 13px; color: ${T.muted}; margin-top: 2px;
`;

/* ─── Tabela ─── */
const TableCard = styled.div`
  background: ${T.card}; border: 1px solid ${T.border};
  border-radius: ${T.radius}; overflow: hidden;
`;

const TableWrap = styled.div`overflow-x: auto;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
`;

const Th = styled.th`
  padding: 11px 16px; text-align: left;
  font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
  text-transform: uppercase; color: ${T.faint};
  background: #f9fafb; border-bottom: 1px solid ${T.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 13px 16px; border-bottom: 1px solid #f3f4f6;
  color: ${T.text}; vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.12s;
  &:hover td { background: #fafafa; }
  &:last-child td { border-bottom: none; }
`;

const TableFooter = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-top: 1px solid ${T.border};
`;

/* ─── Células ─── */
const CompanyName = styled.div`
  font-weight: 600; color: ${T.text}; font-size: 13px;
`;
const CompanyDoc = styled.div`
  font-size: 11px; color: ${T.faint}; margin-top: 2px; font-family: monospace;
`;
const AddressLine = styled.div`
  font-size: 12px; color: ${T.muted}; line-height: 1.5;
`;

const CountBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  background: #f0fdf4; color: #15803d;
  border: 1px solid #bbf7d0;
  padding: 3px 10px; border-radius: 99px;
  font-size: 12px; font-weight: 600;
`;

const ActionGroup = styled.div`
  display: flex; align-items: center; gap: 2px;
`;

/* ─── Paginação ─── */
const PagBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 7px 12px; border-radius: ${T.radiusSm};
  font-size: 12px; font-weight: 600; font-family: inherit;
  color: ${T.muted}; background: transparent; border: 1px solid ${T.border};
  cursor: pointer; transition: all 0.15s;
  &:hover:not(:disabled) { background: #f3f4f6; color: ${T.text}; border-color: ${T.borderHover}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PagInfo = styled.span`
  font-size: 12px; color: ${T.faint};
`;

/* ─── Dialog de exclusão ─── */
const DialogBtnWrap = styled.div`
  display: flex; gap: 8px; padding: 8px 16px 16px;
`;

/* ─── Modal de link ─── */
const LinkModalInner = styled.div`
  text-align: center; padding: 8px 0;
`;
const LinkModalTitle = styled.div`
  font-size: 15px; font-weight: 700; color: ${T.text}; margin-bottom: 4px;
`;
const LinkModalSub = styled.div`
  font-size: 13px; color: ${T.muted}; margin-bottom: 20px;
`;
const LinkBtns = styled.div`
  display: flex; gap: 12px; justify-content: center; margin-bottom: 16px;
`;
const LinkIconBtn = styled.button`
  width: 48px; height: 48px; border-radius: 50%;
  border: none; cursor: pointer; display: flex;
  align-items: center; justify-content: center;
  font-size: 22px; transition: transform 0.15s;
  &:hover { transform: scale(1.08); }
`;
const CopiedMsg = styled.div`
  font-size: 12px; font-weight: 600; color: #16a34a; margin-top: 8px;
`;

/* ─── Componente ─── */
function EmpresasApp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { empresas, loading, page, per_page, total } = useSelector((s) => s.empresas);

  const [openModalCadastro, setOpenModalCadastro] = useState(false);
  const [openDialogExclusao, setOpenDialogExclusao] = useState(false);
  const [dialogData, setDialogData] = useState({});
  const [filtro, setFiltro] = useState("");
  const [filtroAtualizado, setFiltroAtualizado] = useState("");
  const [modalCredenciado, setModalCredenciado] = useState(false);
  const [empresaCredenciado, setEmpresaCredenciado] = useState(null);
  const [modalLink, setModalLink] = useState(false);
  const [dataModalLink, setDataModalLink] = useState({});
  const [linkCopy, setLinkCopy] = useState(false);

  async function GetDataLoad() {
    await loadEmpresas({ page, per_page, filtro: filtroAtualizado, status: "A" });
    setFiltroAtualizado("");
  }

  const navigatePages = (v) => dispatch({ type: types.EMPRESAS_DATA_PAGE, payload: v });

  useEffect(() => {
    const t = setTimeout(() => { filtro === "" ? GetDataLoad() : setFiltroAtualizado(filtro); }, 500);
    return () => clearTimeout(t);
  }, [filtro, page]);

  useEffect(() => { if (filtroAtualizado !== "") GetDataLoad(); }, [filtroAtualizado, page]);

  async function LoadCliente(data) {
    try { await loadClientesEmpresa(data.id); navigate(`/empresas/${data.id}`); } catch (e) { console.log(e); }
  }

  async function ExcluirEmpresaEClientes() {
    try { await ActionExcluirEmpresaEClientes(dialogData.id); await GetDataLoad(); setOpenDialogExclusao(false); navigate("/empresas"); } catch (e) { console.log(e); }
  }

  async function SetEmpresaCadastroApp(data) {
    try { await ActionDefinirEmpresaCadastroApp({ empresa_id: data.id }); await GetDataLoad(); } catch (e) { console.log(e); }
  }

  function ModalGetLink(data) {
    const token = localStorage.getItem("white_label_token");
    setModalLink(true);
    setDataModalLink({ ...data, link: `${data.cpf_cnpj}/${token}` });
  }

  async function copyLinkToPaste() {
    await navigator.clipboard.writeText(`https://aliviaapp.com.br/${dataModalLink.link}`);
    setLinkCopy(true);
    setTimeout(() => setLinkCopy(false), 3000);
  }

  const handleClickWhatsapp = () =>
    window.open(`https://api.whatsapp.com/send?text=Faça seu cadastro e aproveite os benefícios https://aliviaapp.com.br/${dataModalLink.link}`, "_blank");

  return (
    <DashboardLayout>
      <Loading loading={loading} />
      <DashboardNavbar
        filtroNome="Buscar empresa pelo nome"
        valueFiltro={filtro}
        onChangeFiltro={(e) => setFiltro(e.target.value)}
      />

      <MDBox pt={4} pb={3}>
        <PageWrap>
          <PageHeader>
            <div>
              <PageTitle>Empresas</PageTitle>
              <PageSub>{total > 0 ? `${total} empresa${total !== 1 ? "s" : ""} cadastrada${total !== 1 ? "s" : ""}` : "Nenhuma empresa cadastrada"}</PageSub>
            </div>
            <BtnPrimary onClick={() => setOpenModalCadastro(true)}>
              + Cadastrar empresa
            </BtnPrimary>
          </PageHeader>

          <TableCard>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Empresa</Th>
                    <Th>Endereço</Th>
                    <Th>Vidas ativas</Th>
                    <Th>Cadastro</Th>
                    <Th style={{ textAlign: "center" }}>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {empresas.length === 0 ? (
                    <tr>
                      <Td colSpan={5} style={{ textAlign: "center", color: T.faint, padding: "40px 0" }}>
                        Nenhuma empresa encontrada
                      </Td>
                    </tr>
                  ) : (
                    empresas.map((item) => (
                      <Tr key={item.id}>
                        <Td>
                          <CompanyName>{item.nome && formatName(item.nome)}</CompanyName>
                          <CompanyDoc>{item.cpf_cnpj && MaskCPFCNPJ(item.cpf_cnpj)}</CompanyDoc>
                        </Td>
                        <Td>
                          <AddressLine>{item.endereco && formatName(item.endereco)}{item.numero ? `, ${item.numero}` : ""}</AddressLine>
                          <AddressLine>{item.bairro && formatName(item.bairro)} — {item.cidade && formatName(item.cidade)}/{item.uf}</AddressLine>
                        </Td>
                        <Td>
                          <CountBadge>● {item.totalClientes}</CountBadge>
                        </Td>
                        <Td style={{ color: T.muted, fontSize: 12 }}>
                          {dayjs(item.created_at).format("DD/MM/YYYY")}
                        </Td>
                        <Td>
                          <ActionGroup>
                            <CustomTooltip title={item.cadastro_app ? "Empresa escolhida para o app" : "Definir como empresa do app"}>
                              <IconBtn onClick={() => SetEmpresaCadastroApp(item)}>
                                <PhoneIphone style={{ fontSize: 20, color: item.cadastro_app ? T.purple : T.faint }} />
                              </IconBtn>
                            </CustomTooltip>

                            <CustomTooltip title="Link de cadastro">
                              <IconBtn onClick={() => ModalGetLink(item)}>
                                <AddLink style={{ fontSize: 20, color: T.blue }} />
                              </IconBtn>
                            </CustomTooltip>

                            <CustomTooltip title="Acessos credenciados">
                              <IconBtn onClick={() => { setEmpresaCredenciado(item); setModalCredenciado(true); }}>
                                <ManageAccounts style={{ fontSize: 20, color: T.purple }} />
                              </IconBtn>
                            </CustomTooltip>

                            <CustomTooltip title="Ver clientes">
                              <IconBtn onClick={() => LoadCliente(item)}>
                                <ReadMore style={{ fontSize: 20, color: T.green }} />
                              </IconBtn>
                            </CustomTooltip>

                            <CustomTooltip title="Excluir empresa">
                              <IconBtn onClick={() => { setDialogData(item); setOpenDialogExclusao(true); }}
                                style={{ "&:hover": { background: "#fef2f2", borderColor: "#fca5a5" } }}>
                                <DeleteOutlineOutlined style={{ fontSize: 20, color: T.danger }} />
                              </IconBtn>
                            </CustomTooltip>
                          </ActionGroup>
                        </Td>
                      </Tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrap>

            <TableFooter>
              <div style={{ display: "flex", gap: 8 }}>
                <PagBtn disabled={+page === 1} onClick={() => navigatePages(+page - 1)}>
                  <KeyboardArrowLeftOutlined style={{ fontSize: 16 }} /> Anterior
                </PagBtn>
                <PagBtn disabled={empresas?.length === 0} onClick={() => navigatePages(+page + 1)}>
                  Próximo <KeyboardArrowRightOutlined style={{ fontSize: 16 }} />
                </PagBtn>
              </div>
              <PagInfo>Página {page} · {total} empresa{total !== 1 ? "s" : ""}</PagInfo>
            </TableFooter>
          </TableCard>
        </PageWrap>

        <EmpresasCadastro open={openModalCadastro} close={() => setOpenModalCadastro(false)} updateList={GetDataLoad} />

        <ModalCriarCredenciado
          open={modalCredenciado}
          empresa={empresaCredenciado}
          onClose={() => { setModalCredenciado(false); setEmpresaCredenciado(null); }}
        />

        {/* Modal link */}
        <ModalComponent open={modalLink} width="auto">
          <LinkModalInner>
            <LinkModalTitle>Compartilhar link de cadastro</LinkModalTitle>
            <LinkModalSub>{dataModalLink.nome && formatName(dataModalLink.nome)}</LinkModalSub>
            <Divider style={{ marginBottom: 20 }} />
            <LinkBtns>
              <Tooltip title="Copiar link">
                <LinkIconBtn onClick={copyLinkToPaste} style={{ background: "#2563eb" }}>
                  <AddLink style={{ color: "#fff", fontSize: 22 }} />
                </LinkIconBtn>
              </Tooltip>
              <Tooltip title="Enviar pelo WhatsApp">
                <LinkIconBtn onClick={handleClickWhatsapp} style={{ background: "#25d366" }}>
                  <WhatsApp style={{ color: "#fff", fontSize: 22 }} />
                </LinkIconBtn>
              </Tooltip>
            </LinkBtns>
            {linkCopy && <CopiedMsg>✓ Link copiado!</CopiedMsg>}
            <BtnOutline style={{ marginTop: 16, width: "100%", justifyContent: "center" }} onClick={() => setModalLink(false)}>
              Fechar
            </BtnOutline>
          </LinkModalInner>
        </ModalComponent>

        {/* Dialog excluir */}
        <Dialog
          open={openDialogExclusao}
          onClose={() => setOpenDialogExclusao(false)}
          PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: 16 }}>
            Excluir empresa?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: 14, color: "#6b7280" }}>
              Esta ação removerá <strong style={{ color: "#111" }}>{dialogData.nome && formatName(dialogData.nome)}</strong> e todos os clientes vinculados permanentemente. Não é possível desfazer.
            </DialogContentText>
          </DialogContent>
          <DialogBtnWrap>
            <BtnOutline style={{ flex: 1, justifyContent: "center" }} onClick={() => setOpenDialogExclusao(false)}>
              Cancelar
            </BtnOutline>
            {!loading && (
              <Btn
                onClick={ExcluirEmpresaEClientes}
                style={{ flex: 1, justifyContent: "center", background: T.danger, color: "#fff", padding: "9px 18px", borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600 }}
              >
                Sim, excluir
              </Btn>
            )}
          </DialogBtnWrap>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EmpresasApp;